import { useCallback } from 'react';

import type {
  ApprovalAction,
  ApprovalData,
  ApprovalType,
} from 'ui-product-approvals-common';

import type {
  AppHubUserRecord,
  AppTeamRecord,
  BreezeAgentRecord,
} from '../types/CrmSearchUser';
import type { ApprovalStepInput } from 'ui-product-approvals-common/types/ApprovalData';
import { captureException } from '../utils/sentryUtils';
import { assertApprovalDataResolved } from '../utils/assertApprovalDataResolved';
import { toApprovalData } from '../utils/approvalResponseAdapters';
import { useUpdateApprovalMutation } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { GET_LATEST_APPROVAL_QUERY } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.dfc.new';
import { APPROVAL_TYPE_TO_OBJECT_TYPE_ID } from 'ui-product-approvals-common/constants/approvalTypeIds';

interface UseUpdateApprovalArgs {
  approvalType: ApprovalType;
  approvalAction: ApprovalAction;
  approvableObjectId: number | undefined;
  usersRecord: AppHubUserRecord;
  agentsRecord: BreezeAgentRecord;
  teamsRecord: AppTeamRecord;
  resolvedData: ApprovalData;
}

export const useUpdateApproval = ({
  approvalType,
  approvalAction,
  approvableObjectId,
  usersRecord,
  agentsRecord,
  teamsRecord,
  resolvedData,
}: UseUpdateApprovalArgs) => {
  const [updateApprovalMutation] = useUpdateApprovalMutation();

  const update = useCallback(
    ({
      approvalSteps,
      dueDateTimestamp,
      title,
    }: {
      approvalSteps: ApprovalStepInput[];
      dueDateTimestamp?: number;
      title?: string;
    }): Promise<ApprovalData> => {
      const validated = assertApprovalDataResolved(
        'useUpdateApproval',
        resolvedData,
        approvableObjectId
      );
      return updateApprovalMutation({
        variables: {
          approvalId: validated.resolvedData.id,
          approvalSteps: approvalSteps.map((step) =>
            step.order === undefined
              ? { actor: step.actor }
              : { actor: step.actor, order: step.order }
          ),
          ...(dueDateTimestamp !== undefined && {
            dueDate: dueDateTimestamp,
          }),
          ...(title && { title }),
        },
        update: (cache, { data: mutationData }) => {
          const approvalData = mutationData?.result?.approval;
          if (approvalData) {
            cache.writeQuery({
              query: GET_LATEST_APPROVAL_QUERY,
              variables: {
                approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
                approvalAction,
                approvableObjectId: validated.approvableObjectId,
              },
              data: { result: { approval: approvalData } },
            });
          }
        },
      })
        .then((result) => {
          const approval = result?.data?.result?.approval;
          if (approval) {
            return toApprovalData(
              approval,
              usersRecord,
              agentsRecord,
              approvalType,
              approvalAction,
              teamsRecord
            );
          } else {
            throw new Error(
              'updateApproval succeeded but returned no approval data'
            );
          }
        })
        .catch((error) => {
          captureException(error, {
            extra: {
              fingerprint: 'useApprovalData-update',
              approvalType,
              approvalAction,
              approvalId: validated.resolvedData.id,
            },
          });
          throw error;
        });
    },
    [
      updateApprovalMutation,
      resolvedData,
      usersRecord,
      agentsRecord,
      teamsRecord,
      approvalType,
      approvalAction,
      approvableObjectId,
    ]
  );

  return update;
};
